// IAM admin client — browser-friendly wrapper over the AWS SDK's
// IAMClient, covering the two things the platform needs when it
// materializes a project's role taxonomy (provisioning + role sync):
//
//   1. Create-or-update IAM roles whose trust policy lets the project's
//      Identity Pool assume them (`ensureRole`).
//   2. Attach per-role inline policies — the rendered baseline/capability/
//      grants statements from policy-templates.ts (`putInlinePolicy`).
//
// There is no IAM OIDC provider anymore (it only served the retired
// direct-STS roles) — the Identity Pool's enhanced flow validates User
// Pool tokens itself.

import {
  CreateRoleCommand,
  GetRoleCommand,
  IAMClient,
  PutRolePolicyCommand,
  TagRoleCommand,
  UpdateAssumeRolePolicyCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-iam@3.1054.0/+esm";

export interface IamAuthConfig {
  region: string;
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export class IamAdmin {
  private client: IAMClient;

  constructor(cfg: IamAuthConfig) {
    this.client = new IAMClient({
      region: cfg.region,
      credentials: async () => cfg.credentials(),
    });
  }

  /** Create-or-update a role. The trust policy is the only field we
   *  refresh on update; permissions policies are attached separately
   *  via `putInlinePolicy` so the lifecycle of those is independent. */
  async ensureRole(args: {
    name: string;
    description?: string;
    /** Trust policy (who can AssumeRole). For our model:
     *    Principal.Federated = cognito-identity.amazonaws.com
     *    Action = sts:AssumeRoleWithWebIdentity (+ sts:TagSession)
     *    Condition restricts to the project's Identity Pool. */
    trustPolicy: object;
    /** Tags to apply at create time AND re-apply on existing roles
     *  (TagRole is additive — same key gets overwritten). */
    tags?: Array<{ Key: string; Value: string }>;
  }): Promise<string> {
    try {
      const got = await this.client.send(new GetRoleCommand({ RoleName: args.name }));
      log(`role '${args.name}' already exists — updating trust policy`);
      await this.client.send(
        new UpdateAssumeRolePolicyCommand({
          RoleName: args.name,
          PolicyDocument: JSON.stringify(args.trustPolicy),
        }),
      );
      if (args.tags?.length) {
        await this.client.send(new TagRoleCommand({ RoleName: args.name, Tags: args.tags }));
      }
      return got.Role?.Arn ?? "";
    } catch (e) {
      const code = (e as { name?: string })?.name;
      if (code !== "NoSuchEntityException") throw e;
    }
    log(`creating role '${args.name}'`);
    const res = await this.client.send(
      new CreateRoleCommand({
        RoleName: args.name,
        Description: args.description,
        AssumeRolePolicyDocument: JSON.stringify(args.trustPolicy),
        Tags: args.tags,
      }),
    );
    const arn = res.Role?.Arn;
    if (!arn) throw new Error("CreateRole returned no ARN");
    return arn;
  }

  /** Attach an inline policy to a role. Inline is simpler than managed
   *  for per-project policies — they live and die with the role and
   *  don't need separate cleanup. */
  async putInlinePolicy(args: {
    roleName: string;
    policyName: string;
    policyDocument: object;
  }): Promise<void> {
    log(`putting inline policy '${args.policyName}' on role '${args.roleName}'`);
    await this.client.send(
      new PutRolePolicyCommand({
        RoleName: args.roleName,
        PolicyName: args.policyName,
        PolicyDocument: JSON.stringify(args.policyDocument),
      }),
    );
  }
}

function log(msg: string): void {
  console.log(`[iam] ${msg}`);
}
